'use client';

import {
  FeatherAlertTriangle,
  FeatherClock,
  FeatherInfo,
  FeatherLock,
  FeatherX,
  FeatherZap,
} from '@subframe/core';
import React from 'react';

import { Button } from './Button';
import { IconButton } from './IconButton';

export type AvisoPlanoVariant =
  | 'trialAcabando'
  | 'trialTerminou'
  | 'saldoBaixo'
  | 'esgotado'
  | 'franquiaEstourada'
  | 'semPlano'
  | 'semConvenio';

export interface AvisoPlanoFlutuanteProps {
  variant: AvisoPlanoVariant;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

const VARIANT_STYLE: Record<
  AvisoPlanoVariant,
  {
    wrapper: string;
    iconWrapper: string;
    icon: React.ReactNode;
    actionVariant: 'brand-primary' | 'brand-secondary';
  }
> = {
  trialAcabando: {
    wrapper: 'border-warning-200 bg-warning-50',
    iconWrapper: 'bg-warning-100 text-warning-700',
    icon: <FeatherZap className="h-4 w-4" />,
    actionVariant: 'brand-primary',
  },
  trialTerminou: {
    wrapper: 'border-error-200 bg-error-50',
    iconWrapper: 'bg-error-100 text-error-700',
    icon: <FeatherClock className="h-4 w-4" />,
    actionVariant: 'brand-primary',
  },
  saldoBaixo: {
    wrapper: 'border-warning-200 bg-warning-50',
    iconWrapper: 'bg-warning-100 text-warning-700',
    icon: <FeatherAlertTriangle className="h-4 w-4" />,
    actionVariant: 'brand-primary',
  },
  esgotado: {
    wrapper: 'border-error-200 bg-error-50',
    iconWrapper: 'bg-error-100 text-error-700',
    icon: <FeatherAlertTriangle className="h-4 w-4" />,
    actionVariant: 'brand-primary',
  },
  franquiaEstourada: {
    wrapper: 'border-neutral-200 bg-default-background',
    iconWrapper: 'bg-brand-50 text-brand-700',
    icon: <FeatherInfo className="h-4 w-4" />,
    actionVariant: 'brand-secondary',
  },
  semPlano: {
    wrapper: 'border-neutral-200 bg-default-background',
    iconWrapper: 'bg-brand-50 text-brand-700',
    icon: <FeatherLock className="h-4 w-4" />,
    actionVariant: 'brand-primary',
  },
  // Informativo, não bloqueio: sem convênio a criação segue (com custa avulsa), então o
  // cadeado e o botão primário do `semPlano` diriam o contrário do que a tela permite.
  semConvenio: {
    wrapper: 'border-neutral-200 bg-default-background',
    iconWrapper: 'bg-brand-50 text-brand-700',
    icon: <FeatherInfo className="h-4 w-4" />,
    actionVariant: 'brand-secondary',
  },
};

export const AvisoPlanoFlutuante: React.FC<AvisoPlanoFlutuanteProps> = ({
  variant,
  title,
  description,
  actionLabel,
  onAction,
  onDismiss,
}) => {
  const [visible, setVisible] = React.useState(true);
  const style = VARIANT_STYLE[variant];

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed right-6 bottom-6 z-50 w-[340px] max-w-[calc(100vw-3rem)]"
    >
      <div
        className={`flex w-full items-start gap-3 rounded-[16px] border border-solid p-4 shadow-lg ${style.wrapper}`}
      >
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${style.iconWrapper}`}
        >
          {style.icon}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-body-bold font-body-bold text-default-font">{title}</p>
          <p className="text-caption font-caption text-subtext-color">{description}</p>
          {actionLabel && onAction ? (
            <div className="mt-2">
              <Button variant={style.actionVariant} size="small" onClick={onAction}>
                {actionLabel}
              </Button>
            </div>
          ) : null}
        </div>
        <IconButton
          size="small"
          icon={<FeatherX />}
          onClick={handleDismiss}
          aria-label="Fechar aviso"
        />
      </div>
    </div>
  );
};
